import * as React from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { notify, eventBus, type EventBusRegistry } from 'services';
import { useFetchRestrictionEvents, useJustifyRestrictionEvent } from 'hooks/api/restricted-products';
import {
  useRestrictionEventSearchParams,
} from 'hooks/store/restricted-products/use-restriction-event-search-params';
import { Scaffold } from 'layouts';
import { DataTableV2 } from 'components/ui/DataTableV2';
import { PageHeaderForFilters } from 'components/ui/page-header-for-filters';

import { useRestrictedProductColumns } from './hooks';
import {
  RestrictionEventsFilter,
  JustifyEventDrawer,
  type JustifyProductRestrictionDrawerRef,
  ShowEventJustificationDrawer,
  type ShowEventsJustificationDrawerRef,
  RestrictionEventsReport,
} from './components';
import { TabbedNavigation } from '../shared-components';

export const ListRestrictionEventsPage = () => {
  const {
    filters,
    paginationControl,
    updatePaginationControl,
    clearState,
    updateFilters,
  } = useRestrictionEventSearchParams();
  const { fetchRestrictionEvents } = useFetchRestrictionEvents();
  const { justifyEvent } = useJustifyRestrictionEvent();

  const columns = useRestrictedProductColumns();
  const queryClient = useQueryClient();
  const justifyEventDrawerRef = React.useRef<JustifyProductRestrictionDrawerRef>(null);
  const showEventJustificationDrawerRef = React.useRef<ShowEventsJustificationDrawerRef>(null);

  const productsQuery = useQuery({
    queryKey: ['fetch-restriction-events', filters, paginationControl],
    queryFn: () => fetchRestrictionEvents({ filters, paginationControl }),
    onError: (error: any) => {
      notify('error', error?.message);
    },
  });

  const justifyEventMutation = useMutation({
    mutationKey: ['justify-event'],
    mutationFn: justifyEvent,
    onSuccess: async () => {
      await queryClient.invalidateQueries(['fetch-restriction-events']);
    },
    onSettled: () => {
      justifyEventDrawerRef.current?.close();
    },
  });

  React.useEffect(() => {
    const registries: EventBusRegistry[] = [];

    registries.push(eventBus.subscribe('open-justify-event-drawer', (data) => {
      console.log(data);
      justifyEventDrawerRef.current?.open(data);
    }));

    registries.push(eventBus.subscribe('open-show-event-justification-drawer', (data) => {
      showEventJustificationDrawerRef.current?.open(data);
    }));

    return () => {
      registries.forEach(reg => reg.unsubscribe());
    };
  }, []);

  return (
    <Scaffold>
      <TabbedNavigation />
      <JustifyEventDrawer
        ref={justifyEventDrawerRef}
        onSubmit={justifyEventMutation.mutate}
        loading={justifyEventMutation.isLoading}
      />
      <ShowEventJustificationDrawer ref={showEventJustificationDrawerRef} />
      <PageHeaderForFilters
        headerTitle={<div className={'w-full font-GilroySemibold'}>Filtro</div>}
        className={'mb-4'}
      >
        <RestrictionEventsFilter
          onSubmitFilters={updateFilters}
          onResetFilters={clearState}
        />
      </PageHeaderForFilters>
      <DataTableV2
        columns={columns}
        data={productsQuery.data?.data || []}
        isLoading={productsQuery.isLoading}
        manualPagination
        onPaginationChange={updatePaginationControl}
        onSort={updatePaginationControl}
        stickyHeader
        tableWrapperClassName={'max-h-[calc(100vh-360px)] min-h-[240px]'}
        renderTopBar={() => (
          <div className={'flex w-full justify-end'}>
            <RestrictionEventsReport filters={filters} />
          </div>
        )}
        pagination={{
          ...productsQuery?.data?.pagination,
          currentPage: paginationControl?.page,
          perPage: paginationControl?.limit,
        }}
      />
    </Scaffold>
  );
};
